<template>
  <el-dialog
    :visible="visible"
    width="40%"
    :close-on-click-modal="false"
    @close="handleClose"
    @open="onOpen"
  >
    <ContentHeader
      slot="title"
      :title="
        isEdit
          ? $t('stability.timePoints.editTitle')
          : $t('stability.timePoints.addTitle')
      "
    />

    <ValidationObserver ref="observer">
      <el-form
        label-position="top"
        @submit.native.prevent="submit"
      >
        <FormItem
          vid="token"
          rules="required|alpha_dash"
          :label="$t('stability.token')"
        >
          <el-input
            ref="tokenInput"
            v-model.trim="form.token"
            :placeholder="$t('stability.timePoints.tokenPlaceholder')"
          />
        </FormItem>

        <FormItem :label="$t('stability.timePoints.description')">
          <el-input
            v-model="form.description"
            :placeholder="$t('stability.timePoints.descriptionPlaceholder')"
          />
        </FormItem>

        <FormItem
          rules="required"
          :label="$t('stability.timePoints.fromDate')"
        >
          <el-date-picker
            v-model="form.fromDate"
            type="date"
            value-format="yyyy-MM-dd"
            :placeholder="$t('stability.timePoints.fromDate')"
          />
        </FormItem>
      </el-form>
    </ValidationObserver>

    <span slot="footer">
      <el-button
        :disabled="loading"
        @click="handleClose"
      >
        {{ $t('global.cancel') }}
      </el-button>
      <el-button
        type="primary"
        :loading="loading"
        @click="submit"
      >
        {{ $t('global.confirm') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import { stabilityApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';

  const emptyForm = () => ({ token: '', description: '', fromDate: null });

  export default {
    name: 'TimePointDialog',
    components: { FormItem, ContentHeader },

    props: {
      visible: { type: Boolean, required: true },
      configId: { type: Number, required: true },
      // null in creation, the time point being edited otherwise
      timePoint: { type: Object, default: null },
      // Every token already defined on this configuration, the edited one included
      existingTokens: { type: Array, default: () => [] },
    },

    data() {
      return {
        loading: false,
        form: emptyForm(),
      };
    },

    computed: {
      isEdit() {
        return Boolean(this.timePoint);
      },
      // The token of the time point under edit is not a duplicate of itself
      takenTokens() {
        if (!this.isEdit) return this.existingTokens;
        return this.existingTokens.filter(
          (token) => token !== this.timePoint.token
        );
      },
    },

    methods: {
      onOpen() {
        this.form = this.isEdit
          ? {
              token: this.timePoint.token,
              description: this.timePoint.description || '',
              // The picker works on plain dates; the API returns a timestamp,
              // read back in local time (see formatDate in TimePointSection)
              fromDate: moment(this.timePoint.fromDate).format('YYYY-MM-DD'),
            }
          : emptyForm();
        // The dialog body is rendered on the first open, so both refs are only
        // reachable on the next tick
        this.$nextTick(() => {
          if (this.$refs.observer) this.$refs.observer.reset();
          if (this.$refs.tokenInput) this.$refs.tokenInput.focus();
        });
      },

      handleClose() {
        this.$emit('update:visible', false);
        this.form = emptyForm();
        if (this.$refs.observer) this.$refs.observer.reset();
      },

      async submit() {
        const valid = await this.$refs.observer.validate();
        if (!valid) return;

        // Rejected here, before the call: the API answers 409 on the same case
        if (this.takenTokens.includes(this.form.token)) {
          this.$refs.observer.setErrors({
            token: [this.$t('stability.timePoints.duplicateToken')],
          });
          return;
        }

        const description = this.form.description.trim();
        const payload = {
          token: this.form.token,
          description: description || null,
          fromDate: moment.utc(this.form.fromDate).toDate(),
        };

        this.loading = true;
        try {
          if (this.isEdit) {
            await stabilityApi.updateTimePoint(
              this.configId,
              this.timePoint.id,
              payload
            );
          } else {
            await stabilityApi.createTimePoint(this.configId, payload);
          }
          this.$emit('saved');
          this.handleClose();
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>
